import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import clsx from 'clsx';
import PersonIcon from '@mui/icons-material/Person';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import { Tooltip } from '@katalon-studio/katalon-ui/v2';
import { useSelector } from 'react-redux';
import React, { useImperativeHandle } from 'react';
import { Account } from '../../../models';
import { getUserAvatar } from '../../../utils/Users';
import { useConfig } from '../../../config';
import { MAX_ACCOUNT_NAME_LENGTH } from '../../../utils/Constants';
import { fromAuth } from '../../../store';
import { useCheckPermission } from '../../../hook/useCheckPermission';
import { useQuery } from '../../../routes';

const useStyles = makeStyles(theme => ({
  menuItems: {
    maxHeight: 250,
    overflow: 'auto',
    padding: theme.spacing(1, 0),
  },
  menuItem: {
    padding: theme.spacing(0, 2),
  },
  userBox: {
    width: '100%',
    padding: theme.spacing(0),
  },
  userAvatar: {
    height: 32,
    width: 32,
    margin: theme.spacing(1, 1, 1, 0),
  },
}));

export const AccountItem = React.forwardRef((
  {
    account,
    open,
    handleSwitchAccount,
  }: {
    account: Account,
    open: boolean,
    handleSwitchAccount: (account: Account) => () => void,
  },
  ref,
) => {
  const { config } = useConfig();
  const user = useSelector(fromAuth.selectUser);
  const classes = useStyles();
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const { allowed, loading } = useCheckPermission(account);

  useImperativeHandle(ref, () => ({ loading, allowed }));

  const renderItem = () => (
    <MenuItem
      className={classes.menuItem}
      onClick={handleSwitchAccount(account)}
      sx={{
        width: '100%',
      }}
    >
      <Box
        className={classes.userBox}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        width="100%"
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          width="15%"
        >
          <Avatar
            id="sidebar-user-avatar"
            src={getUserAvatar(user?.avatar, config?.publicUrl)}
            className={clsx(
              classes.userAvatar,
              { [classes.userAvatar]: open },
            )}
          >
            <PersonIcon />
          </Avatar>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          width="80%"
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            width="100%"
          >
            <Typography
              variant="body1"
              fontSize={14}
              fontWeight={600}
            >
              <FormattedMessage
                id="layout.sidebar.user.switch.account.id"
                values={{ accountId: account.id }}
              />
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            width="100%"
          >
            <Typography
              variant="body2"
              fontSize={12}
              fontWeight={500}
              width="100%"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              <FormattedMessage
                id="layout.sidebar.user.switch.account.name"
                values={{ accountName: account.name }}
              />
            </Typography>
          </Box>
        </Box>
      </Box>
    </MenuItem>
  );

  const renderAccountItem = () => (account.name.length > MAX_ACCOUNT_NAME_LENGTH
    ? (
      <Tooltip
        key={`account-${account.id}-tooltip`}
        title={account.name}
        placement="left-start"
      >
        {renderItem()}
      </Tooltip>
    )
    : renderItem());

  return account.id !== accountId && allowed ? renderAccountItem() : null;
});
